import { createFileRoute } from "@tanstack/react-router"
import { LogoFull } from "@/components/LogoFull"
import { useEffect, useState } from "react"
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field.tsx"
import { Input } from "@/components/ui/input.tsx"
import { Button } from "@/components/ui/button.tsx"
import { useMutation } from "@tanstack/react-query"
import { authApi } from "@/api/auth.api.ts"
import { CheckCircle } from "lucide-react"
import { usePageTitle } from "@/contexts/PageTitleContext.tsx"
import { useTranslation } from "react-i18next"
import { cn, getStoredLocale } from "@/lib/utils.ts"
import { LanguageSelector } from "@/components/LanguageSelector.tsx"

export const Route = createFileRoute("/_unauthorized/account/forget-password")({
  component: RouteComponent,
})

function RouteComponent() {
  const { setTitle } = usePageTitle()
  const { t } = useTranslation()
  const locale = getStoredLocale()
  const [email, setEmail] = useState<string>("")
  const [emailError, setEmailError] = useState("")
  const [isSent, setIsSent] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const onChangeEmail = (e) => {
    setEmail(e.target.value)
    validateEmail()
  }
  const validateEmail = () => {
    if (email.trim() === "") {
      setEmailError(t("signup.requiredEmail"))
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError(t("signup.emailInvalid"))
    } else {
      setEmailError("")
    }
  }

  const sendResetPassword = useMutation({
    mutationFn: authApi.sendResetPassword,
    onSuccess: () => {
      setIsSent(true)
    },
  })

  const onSubmit = (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    sendResetPassword.mutate(email)
  }

  useEffect(() => {
    setTitle(t("forget.pageTitle"))
  }, [])

  return (
    <div className="flex h-full w-full max-w-md flex-col items-center p-4 text-center">
      <LogoFull />
      {!isSent ? (
        <>
          <h2
            className={cn(
              "mb-4 text-center font-heading text-xl",
              locale === "ar" && "font-arabic!"
            )}
          >
            {t("forget.title")}
          </h2>
          <div className="flex w-full flex-col gap-6">
            <form className="flex w-full flex-col gap-4">
              <FieldGroup className="w-full min-w-sm">
                <Field>
                  <FieldLabel htmlFor="email">{t("login.email")}</FieldLabel>
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@example.com"
                    required
                    className="h-10"
                    value={email}
                    onChange={onChangeEmail}
                    onBlur={validateEmail}
                  />
                  {emailError !== "" && (
                    <FieldError className="text-start">{emailError}</FieldError>
                  )}
                </Field>
              </FieldGroup>
              <FieldGroup>
                <Button
                  type="submit"
                  disabled={isSubmitting || !!emailError || email === ""}
                  onClick={onSubmit}
                  size="lg"
                >
                  {isSubmitting ? t("common.sending") : t("common.send")}
                </Button>
                <Button
                  onClick={() => {
                    window.location.replace("/login")
                  }}
                  type="button"
                  variant="outline"
                  size="lg"
                >
                  {t("common.backToLogin")}
                </Button>
              </FieldGroup>
            </form>
          </div>
        </>
      ) : (
        <div className="m-8 flex flex-col items-center gap-4 text-center font-heading text-lg">
          <CheckCircle size="48" />
          Password Reset Email Sent!
          <span className="font-sans text-sm">{t("forget.successTitle")}</span>
          <div
            className="m-8 font-sans"
            onClick={() => {
              window.location.replace("/login")
            }}
          >
            <Button type="button" size="lg">
              {t("common.LoginNow")}
            </Button>
          </div>
        </div>
      )}
      <div>
        <LanguageSelector />
      </div>
    </div>
  )
}
